import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X, TrendingUp, Package } from 'lucide-react';
import { overlayClose } from '../lib/overlayClose';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (v) => `R$ ${Number(v || 0).toFixed(2)}`;
const qtyFmt = (q, unit) => `${Number(q || 0).toLocaleString('pt-BR', { maximumFractionDigits: 3 })} ${unit || 'un'}`;

export const CategoryProfitModal = ({ category, label, color, range, onClose }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    setData(null); setError('');
    axios.get(`${API}/dashboard/profit-items`, { params: { category, start_date: range.start, end_date: range.end }, withCredentials: true })
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || 'Erro ao carregar itens'));
  }, [category, range.start, range.end]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const periodLabel = range.start === range.end
    ? new Date(`${range.start}T12:00:00`).toLocaleDateString('pt-BR')
    : `${new Date(`${range.start}T12:00:00`).toLocaleDateString('pt-BR')} a ${new Date(`${range.end}T12:00:00`).toLocaleDateString('pt-BR')}`;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" {...overlayClose(onClose)} data-testid="category-profit-modal">
      <div className="bg-white rounded-md w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl border-2" style={{ borderColor: color }}>
        <div className="flex items-start justify-between p-5 border-b border-slate-200">
          <div>
            <p className="text-xs uppercase tracking-wider font-bold text-slate-500 flex items-center gap-1"><TrendingUp size={12} /> Detalhamento por produto</p>
            <h2 className="text-2xl font-black" style={{ color }} data-testid="category-profit-title">{label}</h2>
            <p className="text-sm text-slate-500">Período: {periodLabel} · ordenado do mais vendido para o menos vendido</p>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-800 p-1" data-testid="category-profit-close"><X size={22} /></button>
        </div>

        {data && (
          <div className="grid grid-cols-3 gap-3 px-5 pt-4">
            <div className="rounded-md bg-blue-50 border border-blue-200 p-3"><p className="text-[10px] uppercase tracking-wider font-bold text-blue-700">Receita</p><p className="text-lg font-black text-blue-700">{money(data.total_revenue)}</p></div>
            <div className="rounded-md bg-amber-50 border border-amber-200 p-3"><p className="text-[10px] uppercase tracking-wider font-bold text-amber-700">Custo</p><p className="text-lg font-black text-amber-700">{money(data.total_cost)}</p></div>
            <div className={`rounded-md p-3 border ${data.total_profit >= 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}><p className={`text-[10px] uppercase tracking-wider font-bold ${data.total_profit >= 0 ? 'text-green-700' : 'text-red-700'}`}>Lucro</p><p className={`text-lg font-black ${data.total_profit >= 0 ? 'text-green-700' : 'text-red-700'}`} data-testid="category-profit-total">{money(data.total_profit)}</p></div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-5">
          {error && <p className="text-red-600 text-sm">{error}</p>}
          {!data && !error && <p className="text-slate-400 text-center py-8">Carregando…</p>}
          {data && data.items.length === 0 && <p className="text-slate-400 text-center py-8">Nenhum item vendido nesta categoria no período.</p>}
          {data && data.items.length > 0 && (
            <table className="w-full" data-testid="category-profit-table">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left text-xs uppercase tracking-wider text-slate-500 font-bold">#</th>
                  <th className="px-3 py-2 text-left text-xs uppercase tracking-wider text-slate-500 font-bold">Produto</th>
                  <th className="px-3 py-2 text-right text-xs uppercase tracking-wider text-slate-500 font-bold">Qtd vendida</th>
                  <th className="px-3 py-2 text-right text-xs uppercase tracking-wider text-slate-500 font-bold">Receita</th>
                  <th className="px-3 py-2 text-right text-xs uppercase tracking-wider text-slate-500 font-bold">Custo</th>
                  <th className="px-3 py-2 text-right text-xs uppercase tracking-wider text-slate-500 font-bold">Lucro</th>
                  <th className="px-3 py-2 text-right text-xs uppercase tracking-wider text-slate-500 font-bold">Margem</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {data.items.map((it, idx) => (
                  <tr key={it.product_id || it.product_name} className="hover:bg-slate-50" data-testid={`category-profit-row-${idx}`}>
                    <td className="px-3 py-2 text-sm text-slate-400 font-mono">{idx + 1}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <Package size={14} className="text-slate-400 shrink-0" />
                        <div>
                          <p className="font-medium text-slate-900 leading-tight">{it.product_name}</p>
                          {it.code && <p className="text-[11px] text-slate-400 font-mono">{it.code} · {it.sales_count} venda(s)</p>}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right font-mono font-bold">{qtyFmt(it.quantity, it.unit)}</td>
                    <td className="px-3 py-2 text-right font-mono text-blue-700">{money(it.revenue)}</td>
                    <td className="px-3 py-2 text-right font-mono text-amber-700">{money(it.cost)}</td>
                    <td className={`px-3 py-2 text-right font-mono font-bold ${it.profit >= 0 ? 'text-green-700' : 'text-red-700'}`}>{money(it.profit)}</td>
                    <td className={`px-3 py-2 text-right text-xs font-bold ${it.profit >= 0 ? 'text-green-700' : 'text-red-700'}`}>{it.margin_percent}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};
